import { Message } from "../types";
import useAppStore from "../stores/appStore";
import { hasAuthToken } from "./authToken";

const DB_NAME = "coachmate-chat-cache";
const DB_VERSION = 1;
const STORE_NAME = "message_threads";
const MAX_CACHED_MESSAGES = 100;

type MessageCacheRecord = {
  cacheKey: string;
  messages: Message[];
  updatedAt: number;
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error ?? new Error("IDB open failed"));

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "cacheKey" });
      }
    };

    request.onsuccess = () => resolve(request.result);
  });

const sanitizeMessagesForCache = (messages: Message[]): Message[] =>
  messages
    .filter((message) => {
      const id = message?.message_id;
      return Boolean(id) && !id.startsWith("temp-");
    })
    .slice(-MAX_CACHED_MESSAGES)
    .map((message) => ({
      ...message,
      isStreaming: false,
      isForStreaming: false,
      isPlaceholder: false,
      isLoading: false,
    }));

export const isLessonFeedMessage = (message: Message): boolean => {
  if (!message) return false;
  if (message.type === "tabbed_message") return true;
  return Boolean(
    Array.isArray(message.tabbed_content) && message.tabbed_content.length,
  );
};

export const filterLessonFeedMessages = (messages: Message[]): Message[] =>
  messages.filter(isLessonFeedMessage);

let pendingCacheKey: string | null = null;
let pendingMessages: Message[] | null = null;

export const cancelMessageCacheSave = () => {
  if (saveCacheTimer) {
    clearTimeout(saveCacheTimer);
    saveCacheTimer = null;
  }
  pendingCacheKey = null;
  pendingMessages = null;
};

export const flushMessageCacheSave = async (): Promise<void> => {
  if (saveCacheTimer) {
    clearTimeout(saveCacheTimer);
    saveCacheTimer = null;
  }

  if (pendingCacheKey && pendingMessages?.length) {
    await saveMessagesToCache(pendingCacheKey, pendingMessages);
  }

  pendingCacheKey = null;
  pendingMessages = null;
};

type MessageCacheKeyOverrides = {
  domain?: string;
  focus?: string;
  messageTheme?: string;
};

export const buildMessageCacheKey = (
  overrides?: MessageCacheKeyOverrides,
): string | null => {
  if (!hasAuthToken()) return null;

  const { domain, focus, messageTheme } = useAppStore.getState();
  const resolvedDomain = overrides?.domain ?? domain;
  const resolvedFocus = overrides?.focus ?? focus;
  const resolvedTheme = overrides?.messageTheme ?? messageTheme;
  let userId = "unknown";

  try {
    const sessionCached = sessionStorage.getItem("coachmate-cached-user");
    const stored =
      sessionCached || localStorage.getItem("coachmate-cached-user");
    if (stored) {
      const parsed = JSON.parse(stored) as { id?: string };
      if (parsed?.id) userId = parsed.id;
    }
  } catch {
    // ignore
  }

  return `${userId}|${resolvedDomain}|${resolvedFocus}|${resolvedTheme}`;
};

export const saveMessagesToCache = async (
  cacheKey: string,
  messages: Message[],
): Promise<void> => {
  const sanitized = sanitizeMessagesForCache(messages);
  if (!sanitized.length) return;

  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const record: MessageCacheRecord = {
        cacheKey,
        messages: sanitized,
        updatedAt: Date.now(),
      };
      const request = store.put(record);
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (error) {
    console.error("Failed to save messages to cache:", error);
  }
};

export const loadMessagesFromCache = async (
  cacheKey: string,
): Promise<Message[]> => {
  try {
    const db = await openDb();
    const record = await new Promise<MessageCacheRecord | null>(
      (resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(cacheKey);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          resolve((request.result as MessageCacheRecord | undefined) ?? null);
        };
        tx.onerror = () => reject(tx.error);
      },
    );
    db.close();
    return record?.messages ?? [];
  } catch (error) {
    console.error("Failed to load messages from cache:", error);
    return [];
  }
};

export const clearMessageCache = async (): Promise<void> => {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (error) {
    console.error("Failed to clear message cache:", error);
  }
};

let saveCacheTimer: ReturnType<typeof setTimeout> | null = null;

export const scheduleMessageCacheSave = (messages: Message[]) => {
  if (saveCacheTimer) clearTimeout(saveCacheTimer);

  const cacheKey = buildMessageCacheKey();
  if (!cacheKey) return;

  pendingCacheKey = cacheKey;
  pendingMessages = messages;

  saveCacheTimer = setTimeout(() => {
    void saveMessagesToCache(cacheKey, messages);
    saveCacheTimer = null;
    pendingCacheKey = null;
    pendingMessages = null;
  }, 500);
};
